import { Trash2 } from "lucide-react";

import type { RecognizedDocument } from "@/api/schemas";
import { BadgeStatus, type StatusKind } from "@/components/ui/badge-status";
import { OriginalDocButton } from "./OriginalDocViewer";

/** Mirror of backend DOCUMENT_TYPES (document_recognizer.py) — labels for the
 *  cards + the manual-override dropdown. Keys must match the backend exactly. */
export const DOC_TYPE_META: Record<string, { label: string; slot: string | null }> = {
  sudski_registar: { label: "Rješenje o upisu u sudski registar", slot: "1.1" },
  hka_imenik: { label: "Rješenje o upisu u imenik HKA", slot: "1.2" },
  posebni_uvjeti: { label: "Posebni uvjeti javnopravnog tijela", slot: "1.10" },
  poziv_javnopravnim_tijelima: {
    label: "Poziv javnopravnim tijelima (eKonferencija)",
    slot: "1.10",
  },
  geodetski_elaborat: { label: "Geodetski elaborat / situacija", slot: "1.14" },
  lokacijska_dozvola: { label: "Lokacijska dozvola", slot: null },
  gradevinska_dozvola: { label: "Građevinska dozvola", slot: null },
  katastar_vlasnicki: { label: "Katastar / ZK izvadak", slot: "1.11" },
  rezervacija_kc: {
    label: "Potvrda rezervacije brojeva k.č.",
    slot: "1.15",
  },
  punomoc: { label: "Punomoć investitora", slot: "1.12" },
  ugovor_pravo_gradenja: {
    label: "Ugovor o pravu građenja / dokaz pravnog interesa",
    slot: "1.13",
  },
  uporabna_dozvola: { label: "Uporabna dozvola (postojeća građevina)", slot: null },
  // R3 — drawing sheets (nacrtni dio). No slot: they aren't opći dio,
  // but every upload is saved and listed here regardless.
  situacija_sira: { label: "Šira situacija", slot: null },
  situacija_uza: { label: "Uža situacija", slot: null },
  tlocrt: { label: "Tlocrt etaže", slot: null },
  presjek: { label: "Presjek", slot: null },
  procelje: { label: "Pročelje / fasada", slot: null },
  generirana_izjava_imenovanje: {
    label: "Izjava/imenovanje (aplikacija generira)",
    slot: null,
  },
  nepoznato: { label: "Nepoznat tip dokumenta", slot: null },
};

/** Extracted fields worth showing on a card, with human labels. */
const FIELD_LABELS: Array<[key: string, label: string]> = [
  ["ured_naziv", "ured"],
  ["ured_sjediste", "sjedište"],
  ["arhitekt_ime", "arhitekt"],
  ["hka_broj", "HKA br."],
  ["izdavatelj", "izdavatelj"],
  ["investitor_naziv", "investitor"],
  ["katastarska_cestica", "k.č."],
  ["klasa", "klasa"],
  ["datum", "datum"],
];

function fieldValue(doc: RecognizedDocument, key: string): string | null {
  const f = doc.fields?.[key];
  const v = f && typeof f === "object" ? f.value : null;
  if (v == null || v === "") return null;
  if (Array.isArray(v)) return v.map(String).join(", ");
  return String(v);
}

function confidenceKind(c: number | null | undefined): StatusKind {
  if (c == null) return "warn";
  if (c >= 0.75) return "ok";
  if (c >= 0.5) return "warn";
  return "err";
}

/**
 * One recognized document — classification badge, type-override dropdown,
 * honest diagnoses (multi-document / recognition failure / drawing
 * diagnostics) and the extracted fields. Since F1 this is the EVIDENCE a
 * console row expands to (the standalone "Dokumenti projekta" card it used
 * to live in folded into the Donesi konzola).
 */
export function DocCard({
  doc,
  onOverride,
  onRemove,
}: {
  doc: RecognizedDocument;
  onOverride: (documentType: string) => void;
  onRemove: () => void;
}) {
  const meta = DOC_TYPE_META[doc.document_type] ?? {
    label: doc.document_type,
    slot: doc.target_slot ?? null,
  };
  const slot = doc.target_slot ?? meta.slot;
  const conf = doc.classification_confidence;
  const overridden = Boolean(
    (doc as Record<string, unknown>)._user_overridden,
  );
  const diagnosticsMessage = (
    (doc as Record<string, unknown>)._diagnostics as
      | { message?: string }
      | undefined
  )?.message;
  const recognitionFailed = Boolean(
    (doc as Record<string, unknown>).recognition_error,
  );
  // Classifier honesty: no deterministic signature + weak LLM answer →
  // the user confirms the type instead of the system guessing.
  const typeUncertain =
    Boolean((doc as Record<string, unknown>).type_uncertain) && !overridden;
  const multiDocument = (doc as Record<string, unknown>)._multi_document as
    | { pages?: number; signatures?: Array<[string, string]> }
    | undefined;
  const shownFields = FIELD_LABELS.map(([key, label]) => {
    const v = fieldValue(doc, key);
    return v ? { key, label, value: v } : null;
  }).filter(Boolean) as Array<{ key: string; label: string; value: string }>;
  const bodies = fieldValue(doc, "bodies");

  return (
    <li className="rounded-md border border-stone-200 bg-white px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[0.82rem] font-semibold text-stone-800">
          📄 {doc._source_file ?? "dokument.pdf"}
        </span>
        <BadgeStatus kind={overridden ? "ok" : confidenceKind(conf)}>
          {overridden
            ? "ručno potvrđeno"
            : conf != null
              ? `${Math.round(conf * 100)}%`
              : "?"}
        </BadgeStatus>
        {slot && (
          <span className="text-[0.72rem] font-semibold uppercase tracking-[0.04em] text-ind-700">
            opći dio {slot}
          </span>
        )}
        {typeUncertain && (
          <BadgeStatus kind="warn">potvrdi vrstu</BadgeStatus>
        )}
        <div className="ml-auto flex items-center gap-1">
          {doc.doc_id && (
            <OriginalDocButton docId={doc.doc_id} fileName={doc._source_file} />
          )}
          <button
            type="button"
            onClick={onRemove}
            title="Ukloni dokument"
            className="rounded p-1 text-stone-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-2">
        <label className="text-[0.74rem] text-stone-500">Vrsta:</label>
        <select
          value={doc.document_type}
          onChange={(e) => onOverride(e.target.value)}
          className="rounded border border-stone-300 bg-white px-2 py-1 text-[0.8rem] text-stone-700"
        >
          {Object.entries(DOC_TYPE_META).map(([key, m]) => (
            <option key={key} value={key}>
              {m.label}
              {m.slot ? ` (${m.slot})` : ""}
            </option>
          ))}
        </select>
      </div>

      {/* A bound multi-document PDF (whole Mapa in one file) — say it
          honestly instead of classifying the folder by its first sheet. */}
      {multiDocument && (
        <p className="mt-1.5 rounded bg-amber-50 px-2 py-1 text-[0.76rem] text-amber-800">
          <span className="font-semibold">
            Ovo izgleda kao više dokumenata u jednoj datoteci
          </span>{" "}
          ({multiDocument.pages ?? "?"} str.; prepoznati potpisi:{" "}
          {(multiDocument.signatures ?? [])
            .map(([t]) => DOC_TYPE_META[t]?.label ?? t)
            .join(", ")}
          ). Automatsko razdvajanje još ne radimo — učitaj dokumente
          pojedinačno za pouzdano prepoznavanje.
        </p>
      )}

      {/* R3 — deterministic diagnosis instead of a silent unknown: what
          the system saw on the sheet (vector/scan, sastavnica, mjerilo)
          and what is missing (e.g. no iskaz table). */}
      {diagnosticsMessage && (
        <p
          className={
            doc.document_type === "nepoznato" || recognitionFailed
              ? "mt-1.5 rounded bg-amber-50 px-2 py-1 text-[0.76rem] text-amber-800"
              : "mt-1.5 text-[0.76rem] text-stone-500"
          }
        >
          {recognitionFailed && (
            <span className="font-semibold">
              Automatsko prepoznavanje nije uspjelo — dokument je spremljen;
              vrstu odaberite ručno.{" "}
            </span>
          )}
          {diagnosticsMessage}
        </p>
      )}

      {shownFields.length > 0 && (
        <p className="mt-1.5 text-[0.76rem] text-stone-500">
          {shownFields.map((f, i) => (
            <span key={f.key}>
              {i > 0 && " · "}
              {f.label}: <span className="font-medium text-stone-700">{f.value}</span>
            </span>
          ))}
        </p>
      )}
      {bodies && (
        <p className="mt-1 text-[0.76rem] text-stone-500">
          pozvana tijela: <span className="font-medium text-stone-700">{bodies}</span>
        </p>
      )}
    </li>
  );
}

